import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  DocumentIcon,
  XMarkIcon,
  PlusIcon,
  CheckCircleIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import ImageModal from '../common/ImageModal';
import toast from 'react-hot-toast';
import { customersAPI, usersAPI, followupsAPI, leadsAPI } from '../../services/api';
import { useCompany } from '../../contexts/CompanyContext';

const CustomerDetail = ({ customerId, onBack, onEdit }) => {
  const { companyData } = useCompany();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [leadHistory, setLeadHistory] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    contact: true,
    address: false,
    professional: false,
    customer: false,
    activity: false,
    attachments: false,
    leadHistory: false
  });

  // Modal states
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [followupData, setFollowupData] = useState({
    title: '',
    note: '',
    remindAt: '',
    type: 'reminder',
    priority: 'medium',
    assignedTo: '',
    duration: 30
  });

  // Image modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState(null);
  const [modalFile, setModalFile] = useState(null);

  // Remark modal state
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [activeFollowupForRemark, setActiveFollowupForRemark] = useState(null);

  // Followup highlighting state
  const [highlightedFollowupId, setHighlightedFollowupId] = useState(null);
  const followupRefs = useRef({});

  // Attachments tab state
  const [activeTab, setActiveTab] = useState('preview');

  // Delete modal states
  const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState(false);
  const [showDeleteAttachmentModal, setShowDeleteAttachmentModal] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);

  useEffect(() => {
    fetchCustomer();
    fetchUsers();
    fetchFollowups();
    fetchLeadHistory();
  }, [customerId]);

  // Handle URL parameters for followup highlighting and auto-opening
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const followupId = urlParams.get('followupId');

    if (followupId && followups.length > 0) {
      // Auto-open activity section
      setExpandedSections(prev => ({
        ...prev,
        activity: true
      }));

      // Set highlighted followup
      setHighlightedFollowupId(followupId);

      // Scroll to the followup after a short delay to ensure DOM is rendered
      setTimeout(() => {
        const followupElement = followupRefs.current[followupId];
        if (followupElement) {
          followupElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Add a temporary highlight effect
          followupElement.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
          setTimeout(() => {
            followupElement.style.boxShadow = '';
          }, 3000);
        }
      }, 1000);
    }
  }, [window.location.search, followups]);

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getById(customerId);
      setCustomer(response.data);
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await leadsAPI.getAssignableUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchFollowups = async () => {
    try {
      const response = await followupsAPI.getAll({ customerId });
      setFollowups(response.data.followups || []);
    } catch (error) {
      console.error('Error fetching followups:', error);
    }
  };

  const fetchLeadHistory = async () => {
    try {
      const response = await leadsAPI.getByCustomer(customerId);
      setLeadHistory(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching lead history:', error);
    }
  };

  const handleDeleteCustomer = () => {
    setShowDeleteCustomerModal(true);
  };

  const confirmDeleteCustomer = async () => {
    try {
      await customersAPI.delete(customerId);
      toast.success('Customer deleted successfully');
      onBack();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    } finally {
      setShowDeleteCustomerModal(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await customersAPI.downloadAttachment(customerId, attachment.fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleDeleteAttachment = (attachment) => {
    setDeletingAttachment(attachment);
    setShowDeleteAttachmentModal(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!deletingAttachment) return;

    try {
      await customersAPI.deleteAttachment(customerId, deletingAttachment.fileId);
      setCustomer(prev => ({
        ...prev,
        attachments: prev.attachments.filter(att => att.fileId !== deletingAttachment.fileId)
      }));
      toast.success('Attachment deleted successfully');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setShowDeleteAttachmentModal(false);
      setDeletingAttachment(null);
    }
  };

  const openCreateFollowup = async () => {
    const currentUser = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    })();

    setFollowupData({
      title: '',
      note: '',
      remindAt: '',
      type: 'reminder',
      priority: 'medium',
      assignedTo: '',
      duration: 30
    });
    setFollowupModalOpen(true);
  };

  const handleCreateFollowup = async () => {
    if (!followupData.title.trim() && !followupData.note.trim() && !followupData.remindAt) {
      toast.error('Please add a title, note or reminder time');
      return;
    }

    if (!followupData.assignedTo) {
      toast.error('Please select who this followup should be assigned to');
      return;
    }

    try {
      const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
      })();

      const assignedTo = followupData.assignedTo || currentUser._id;
      const scheduledDate = followupData.remindAt ? new Date(followupData.remindAt).toISOString() : new Date().toISOString();
      const title = followupData.title && followupData.title.trim()
        ? followupData.title.trim()
        : (followupData.note ? (followupData.note.length > 80 ? followupData.note.slice(0,80) + '...' : followupData.note) : `Followup for ${customer.firstName} ${customer.lastName}`);

      await followupsAPI.create({
        title,
        description: followupData.note,
        type: followupData.type || 'reminder',
        priority: followupData.priority || 'medium',
        scheduledDate,
        duration: Number(followupData.duration) || 30,
        assignedTo,
        relatedTo: 'customer',
        relatedId: customerId,
        notes: followupData.note
      });
      toast.success('Followup created successfully');
      setFollowupModalOpen(false);
      fetchFollowups();
    } catch (error) {
      console.error('Error creating followup:', error);
      toast.error('Failed to create followup');
    }
  };

  const handleChangeFollowupStatus = async (followupId, newStatus) => {
    try {
      if (newStatus === 'completed') {
        // server exposes PUT /:id/complete
        await followupsAPI.markComplete(followupId, { notes: '' });
      } else {
        // reopen -> set status back to pending
        await followupsAPI.update(followupId, { status: 'pending' });
      }
      toast.success(`Followup ${newStatus === 'completed' ? 'completed' : 'reopened'}`);
      fetchFollowups();
    } catch (error) {
      console.error('Error updating followup status:', error);
      toast.error('Failed to update followup status');
    }
  };

  const openRemarkModal = (followup) => {
    setActiveFollowupForRemark(followup._id);
    setRemarkText(followup.notes || followup.description || '');
    setRemarkModalOpen(true);
  };

  const handleSaveRemark = async () => {
    if (!activeFollowupForRemark || !remarkText.trim()) return;

    try {
      await followupsAPI.update(activeFollowupForRemark, { notes: remarkText });
      toast.success('Remark saved successfully');
      setRemarkModalOpen(false);
      setActiveFollowupForRemark(null);
      setRemarkText('');
      fetchFollowups();
    } catch (err) {
      console.error('Error saving remark:', err);
      toast.error('Failed to save remark');
    }
  };

  const handleSendWhatsApp = () => {
    if (customer?.phone) {
      // Remove any non-numeric characters and ensure it starts with country code
      const phoneNumber = customer.phone.replace(/\D/g, '');
      // Assuming Indian numbers, add +91 if not present
      const formattedNumber = phoneNumber.startsWith('91') ? phoneNumber : `91${phoneNumber}`;
      window.open(`https://wa.me/${formattedNumber}`, '_blank');
    } else {
      toast.error('No phone number available for this customer');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (filename) => {
    const extension = (filename || '').split('.').pop().toLowerCase();
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const documentTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

    if (imageTypes.includes(extension)) return 'image';
    if (documentTypes.includes(extension)) return 'document';
    return 'other';
  };

  const getFileIcon = (filename) => {
    const extension = (filename || '').split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <DocumentIcon className="h-5 w-5 text-red-500" />;
      case 'doc':
      case 'docx':
        return <DocumentIcon className="h-5 w-5 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <PaperClipIcon className="h-5 w-5 text-green-500" />;
      default:
        return <PaperClipIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // BlobImage helper to fetch preview blob and render object URL
  const BlobImage = ({ customerId, fileId, alt, className }) => {
    const [src, setSrc] = useState(null);

    useEffect(() => {
      const fetchImage = async () => {
        try {
          const response = await customersAPI.downloadAttachment(customerId, fileId);
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
          const url = window.URL.createObjectURL(blob);
          setSrc(url);
        } catch (error) {
          console.error('Error fetching image:', error);
        }
      };

      fetchImage();
    }, [customerId, fileId]);

    if (!src) return <div className={`bg-gray-200 animate-pulse ${className}`} />;
    return <img src={src} alt={alt} className={className} />;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found</p>
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Customers
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-100">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden bg-gradient-to-r from-green-600 via-teal-600 to-emerald-700 shadow-2xl"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black/10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          <div className="relative px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div className="flex items-center space-x-4 sm:space-x-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onBack}
                  className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-white/30 text-sm font-semibold rounded-xl text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="hidden sm:inline">Back to Customers</span>
                  <span className="sm:hidden">Back</span>
                </motion.button>
                <div>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-1">
                      {customer.firstName} {customer.lastName}
                    </h1>
                    <p className="text-green-100 text-sm sm:text-base lg:text-lg">
                      {customer.companyName || 'No Company'}
                    </p>
                  </motion.div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-center space-x-2 sm:space-x-3"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onEdit(customerId)}
                  className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-white/30 text-sm font-semibold rounded-xl text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <PencilIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="hidden sm:inline">Edit Customer</span>
                  <span className="sm:hidden">Edit</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(239, 68, 68, 0.4)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDeleteCustomer}
                  className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-transparent text-sm font-semibold rounded-xl text-white bg-red-500/80 hover:bg-red-500 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="hidden sm:inline">Delete</span>
                </motion.button>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className="relative -mt-4 sm:-mt-6 px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8"
            >
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-green-500/10 to-teal-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('contact')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-xl mr-3 sm:mr-4">
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Contact Information</h3>
                </div>

                {expandedSections.contact ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.contact && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.firstName} {customer.lastName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.email}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.phone || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Secondary Phone</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.secondaryPhone || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 flex items-center">
                          <GlobeAltIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.website ? (
                            <a href={customer.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500">
                              {customer.website}
                            </a>
                          ) : 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.companyName || 'Not provided'}</p>
                      </div>
                    </div>

                    {/* Social Media */}
                    {(customer.socialMedia?.linkedin || customer.socialMedia?.twitter || customer.socialMedia?.facebook || customer.socialMedia?.instagram) && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Social Media</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {customer.socialMedia?.linkedin && (
                            <div>
                              <label className="block text-xs text-gray-500">LinkedIn</label>
                              <a href={customer.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-500">
                                {customer.socialMedia.linkedin}
                              </a>
                            </div>
                          )}
                          {customer.socialMedia?.twitter && (
                            <div>
                              <label className="block text-xs text-gray-500">Twitter</label>
                              <a href={customer.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-500">
                                {customer.socialMedia.twitter}
                              </a>
                            </div>
                          )}
                          {customer.socialMedia?.facebook && (
                            <div>
                              <label className="block text-xs text-gray-500">Facebook</label>
                              <a href={customer.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-500">
                                {customer.socialMedia.facebook}
                              </a>
                            </div>
                          )}
                          {customer.socialMedia?.instagram && (
                            <div>
                              <label className="block text-xs text-gray-500">Instagram</label>
                              <a href={customer.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-500">
                                {customer.socialMedia.instagram}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Lead History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('leadHistory')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl mr-3 sm:mr-4">
                    <UserGroupIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Lead History</h3>
                  {leadHistory.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {leadHistory.length}
                    </span>
                  )}
                </div>
                {expandedSections.leadHistory ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.leadHistory && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
                  >
                    {leadHistory.length === 0 ? (
                      <p className="text-sm text-gray-500">No lead history found</p>
                    ) : (
                      <div className="space-y-3">
                        {leadHistory.map(lead => (
                          <Link
                            key={lead._id}
                            to={`/leads/${lead._id}`}
                            className="block p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 bg-gray-50 hover:bg-blue-50"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <div className="p-2 bg-blue-100 rounded-full">
                                    <UserGroupIcon className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900">
                                      Lead #{lead._id.slice(-6)}
                                    </h4>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      lead.status === 'active' ? 'bg-green-100 text-green-800' :
                                      lead.status === 'converted' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {lead.status || 'Active'}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Custom Fields */}
                                {lead.customFields && Object.keys(lead.customFields).length > 0 && (
                                  <div className="grid grid-cols-2 gap-2 mb-3">
                                    {Object.entries(lead.customFields).slice(0, 4).map(([key, value]) => (
                                      <div key={key} className="text-xs">
                                        <span className="font-medium text-gray-600">{key}:</span>
                                        <span className="ml-1 text-gray-900">{String(value)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="flex items-center justify-between text-xs text-gray-600">
                                  <div className="flex items-center space-x-4">
                                    <div className="flex items-center">
                                      <ClockIcon className="h-3 w-3 mr-1 text-gray-400" />
                                      {formatDate(lead.createdAt)}
                                    </div>
                                    {lead.stage?.name && (
                                      <div className="flex items-center">
                                        <DocumentIcon className="h-3 w-3 mr-1 text-gray-400" />
                                        {lead.stage.name}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center text-blue-600">
                                    <EyeIcon className="h-3 w-3 mr-1" />
                                    View Lead
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Activity / Followups */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-8 py-6 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('activity')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl mr-4">
                    <ClockIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Activity & Followups</h3>
                </div>
                {expandedSections.activity ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.activity && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-8 py-6"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg">
                          <ChatBubbleLeftRightIcon className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">Activity Timeline</span>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={openCreateFollowup}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-sm font-medium rounded-xl hover:from-orange-600 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Followup
                      </motion.button>
                    </div>

                    {followups.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl inline-block mb-4">
                          <ChatBubbleLeftRightIcon className="h-8 w-8 text-orange-400" />
                        </div>
                        <p className="text-gray-500 font-medium">No followups yet</p>
                        <p className="text-sm text-gray-400 mt-1">Start the conversation by adding your first followup</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {followups.map((f, index) => {
                          const typeIcons = {
                            reminder: <ClockIcon className="h-5 w-5 text-white" />,
                            call: <PhoneIcon className="h-5 w-5 text-white" />,
                            email: <EnvelopeIcon className="h-5 w-5 text-white" />,
                            meeting: <CalendarIcon className="h-5 w-5 text-white" />,
                            task: <DocumentIcon className="h-5 w-5 text-white" />
                          };

                          const typeColors = {
                            reminder: 'from-blue-500 to-blue-600',
                            call: 'from-green-500 to-green-600',
                            email: 'from-purple-500 to-purple-600',
                            meeting: 'from-orange-500 to-orange-600',
                            task: 'from-indigo-500 to-indigo-600'
                          };

                          const statusColors = {
                            completed: 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-200',
                            pending: 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border-blue-200'
                          };

                          return (
                            <motion.div
                              key={f._id}
                              ref={(el) => (followupRefs.current[f._id] = el)}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className={`relative ${highlightedFollowupId === f._id ? 'ring-4 ring-blue-400 ring-opacity-50 bg-blue-50/50' : ''}`}
                            >
                              {/* Timeline line */}
                              {index < followups.length - 1 && (
                                <div className="absolute left-6 top-14 w-0.5 h-full bg-gradient-to-b from-orange-200 to-amber-200"></div>
                              )}

                              <div className="flex items-start space-x-4">
                                {/* Activity Icon */}
                                <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br ${typeColors[f.type] || 'from-gray-500 to-gray-600'} rounded-2xl shadow-lg`}>
                                  {typeIcons[f.type] || <ClockIcon className="h-5 w-5 text-white" />}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-sm hover:shadow-md transition-all duration-200">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <h4 className="text-base font-semibold text-gray-900 truncate">{f.title}</h4>
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${f.status === 'completed' ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-200' : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border-blue-200'}`}>
                                            {f.status === 'completed' ? (
                                              <>
                                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                Completed
                                              </>
                                            ) : (
                                              'Pending'
                                            )}
                                          </span>
                                        </div>
                                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                                          <span className="capitalize bg-gray-100 px-2 py-1 rounded-md">{f.type || 'reminder'}</span>
                                          <span>{f.scheduledDate ? formatDate(f.scheduledDate) : `Created ${formatDate(f.createdAt)}`}</span>
                                          {f.assignedTo && (
                                            <span className="flex items-center">
                                              <UserIcon className="h-3 w-3 mr-1" />
                                              {f.assignedTo.firstName || f.assignedTo.name || ''} {f.assignedTo.lastName || ''}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Description */}
                                    {f.description && (
                                      <p className="text-sm text-gray-700 mb-3 leading-relaxed bg-gray-50/50 rounded-lg p-3 border border-gray-100">
                                        {f.description}
                                      </p>
                                    )}

                                    {/* Notes/Remarks */}
                                    {f.notes && (
                                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-100 mb-3">
                                        <div className="flex items-start space-x-2">
                                          <ChatBubbleLeftRightIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                          <div>
                                            <p className="text-xs font-medium text-amber-800 mb-1">Remark</p>
                                            <p className="text-sm text-amber-700">{f.notes}</p>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex items-center justify-end space-x-2">
                                      {f.status !== 'completed' ? (
                                        <>
                                          <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handleChangeFollowupStatus(f._id, 'completed')}
                                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
                                          >
                                            <CheckCircleIcon className="h-3 w-3 mr-1.5" />
                                            Complete
                                          </motion.button>
                                          <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => openRemarkModal(f)}
                                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                                          >
                                            <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1.5" />
                                            Remark
                                          </motion.button>
                                        </>
                                      ) : (
                                        <motion.button
                                          whileHover={{ scale: 1.02 }}
                                          whileTap={{ scale: 0.98 }}
                                          onClick={() => handleChangeFollowupStatus(f._id, 'pending')}
                                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:from-yellow-600 hover:to-amber-700 transition-all duration-200"
                                        >
                                          <XCircleIcon className="h-3 w-3 mr-1.5" />
                                          Reopen
                                        </motion.button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Address Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('address')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl mr-3 sm:mr-4">
                    <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Address Information</h3>
                </div>
                {expandedSections.address ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.address && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.address?.street || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.address?.city || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">State/Province</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.address?.state || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">ZIP/Postal Code</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.address?.zipCode || 'Not provided'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.address?.country || 'Not provided'}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Professional Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('professional')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl mr-3 sm:mr-4">
                    <BuildingOfficeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Professional Information</h3>
                </div>
                {expandedSections.professional ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.professional && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.jobTitle || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.department || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.industry || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Source</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.source || 'Not provided'}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Customer Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('customer')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl mr-3 sm:mr-4">
                    <CurrencyDollarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Customer Information</h3>
                </div>
                {expandedSections.customer ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.customer && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Type</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 capitalize">{customer.customerType || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Company Size</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">{customer.companySize || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Lifetime Value</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.lifetimeValue ? `${companyData?.currency?.symbol || '$'}${customer.lifetimeValue.toLocaleString()}` : 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Revenue</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.annualRevenue ? `${customer.company?.currency?.symbol || '$'}${customer.annualRevenue.toLocaleString()}` : 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Communication Preference</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100 capitalize">
                          {customer.preferences?.communicationMethod || 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Marketing Opt-in</label>
                        <p className="text-sm text-gray-900 bg-gray-50/50 rounded-lg px-3 py-2 border border-gray-100">
                          {customer.preferences?.marketingOptIn ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>

                    {customer.notes && (
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{customer.notes}</p>
                      </div>
                    )}

                    {customer.tags && customer.tags.length > 0 && (
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {customer.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Attachments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              <div
                className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-white/30 cursor-pointer flex items-center justify-between hover:bg-white/20 transition-all duration-300"
                onClick={() => toggleSection('attachments')}
              >
                <div className="flex items-center">
                  <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl mr-3 sm:mr-4">
                    <PaperClipIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Attachments</h3>
                  {customer.attachments && customer.attachments.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      {customer.attachments.length}
                    </span>
                  )}
                </div>
                {expandedSections.attachments ? (
                  <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <AnimatePresence>
                {expandedSections.attachments && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
                  >
                    {customer.attachments && customer.attachments.length > 0 ? (
                      <div>
                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 mb-4">
                          <button
                            onClick={() => setActiveTab('list')}
                            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
                              activeTab === 'list' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            List View
                          </button>
                          <button
                            onClick={() => setActiveTab('preview')}
                            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
                              activeTab === 'preview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            Preview
                          </button>
                        </div>

                        {/* List Tab */}
                        {activeTab === 'list' && (
                          <div className="space-y-3">
                            {customer.attachments.map((attachment) => (
                              <div key={attachment.fileId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center">
                                  {getFileIcon(attachment.originalName)}
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{attachment.originalName}</p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(attachment.size)} • Uploaded {formatDate(attachment.uploadedAt)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleDownloadAttachment(attachment)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    <ArrowDownTrayIcon className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAttachment(attachment)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <XMarkIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                      {/* Preview Tab */}
                      {activeTab === 'preview' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {customer.attachments.map((attachment) => {
                            const fileType = getFileType(attachment.originalName);
                            const isPreviewable = fileType === 'image' || fileType === 'document';

                            return (
                              <div key={attachment.fileId} className="relative group bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200">
                                <div className="aspect-square bg-gray-50 rounded-md mb-2 flex items-center justify-center overflow-hidden">
                                  {fileType === 'image' ? (
                                    <div className="w-full h-full">
                                      <BlobImage
                                        customerId={customerId}
                                        fileId={attachment.fileId}
                                        alt={attachment.originalName}
                                        className="w-full h-full object-cover cursor-pointer"
                                      />
                                      <button
                                        className="absolute inset-0 w-full h-full bg-transparent hover:bg-black/10 transition-colors duration-200"
                                        onClick={async () => {
                                          try {
                                            const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
                                            const url = window.URL.createObjectURL(blob);
                                            setModalSrc(url);
                                            setModalFile(attachment);
                                            setModalOpen(true);
                                          } catch (err) {
                                            console.error('Error opening modal preview:', err);
                                            toast.error('Failed to load preview');
                                          }
                                        }}
                                        title="Click to preview image"
                                      />
                                    </div>
                                  ) : fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf') ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-pink-50 rounded-md border border-red-200">
                                      <div className="text-red-500 mb-2">
                                        {getFileIcon(attachment.originalName)}
                                      </div>
                                      <div className="text-center px-2">
                                        <p className="text-xs font-medium text-red-700 mb-1">PDF Document</p>
                                        <p className="text-xs text-red-600 mb-2">{formatFileSize(attachment.size)}</p>
                                        <button
                                          className="px-3 py-1 bg-red-500 text-white text-xs rounded-md hover:bg-red-600 transition-colors duration-200 shadow-sm"
                                          onClick={async () => {
                                            try {
                                              const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              window.open(url, '_blank');
                                              // Clean up the blob URL after a delay
                                              setTimeout(() => {
                                                window.URL.revokeObjectURL(url);
                                              }, 1000);
                                            } catch (err) {
                                              console.error('Error opening PDF preview:', err);
                                              toast.error('Failed to load PDF preview');
                                            }
                                          }}
                                          title="Click to preview PDF"
                                        >
                                          👁️ Preview PDF
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      {getFileIcon(attachment.originalName)}
                                    </div>
                                  )}
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-gray-900 truncate" title={attachment.originalName}>{attachment.originalName}</p>
                                  <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                                  {isPreviewable && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      {fileType === 'image' ? '🖼️ Click to preview' : fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf') ? '📄 Click to preview' : ''}
                                    </p>
                                  )}
                                </div>

                                {/* Action buttons - shown on hover */}
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                  {isPreviewable && (
                                    <button
                                      onClick={async () => {
                                        if (fileType === 'image') {
                                          try {
                                            const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
                                            const url = window.URL.createObjectURL(blob);
                                            setModalSrc(url);
                                            setModalFile(attachment);
                                            setModalOpen(true);
                                          } catch (err) {
                                            console.error('Error opening modal preview:', err);
                                            toast.error('Failed to load preview');
                                          }
                                        } else if (fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf')) {
                                          try {
                                            const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                            const blob = new Blob([response.data], { type: 'application/pdf' });
                                            const url = window.URL.createObjectURL(blob);
                                            window.open(url, '_blank');
                                            setTimeout(() => {
                                              window.URL.revokeObjectURL(url);
                                            }, 1000);
                                          } catch (err) {
                                            console.error('Error opening PDF preview:', err);
                                            toast.error('Failed to load PDF preview');
                                          }
                                        }
                                      }}
                                      className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors duration-200"
                                      title={fileType === 'image' ? 'Preview Image' : 'Preview PDF'}
                                    >
                                      <EyeIcon className="h-3 w-3" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDownloadAttachment(attachment)}
                                    className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors duration-200"
                                    title="Download"
                                  >
                                    <ArrowDownTrayIcon className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAttachment(attachment)}
                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200"
                                    title="Delete"
                                  >
                                    <XMarkIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-8">No attachments found</p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Sidebar */}
        
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Customer Status */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl mr-4">
                  <UserIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Customer Status</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    customer.status === 'active' ? 'bg-green-100 text-green-800' :
                    customer.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    customer.status === 'suspended' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {customer.status || 'Active'}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    customer.customerType === 'business' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {customer.customerType === 'business' ? 'Business' : 'Individual'}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Customer Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mr-4">
                  <ClockIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Customer Information</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Created:</span>
                  <span className="ml-2 text-gray-900">{formatDate(customer.createdAt)}</span>
                </div>
                <div className="flex items-center text-sm">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="ml-2 text-gray-900">{formatDate(customer.updatedAt)}</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl mr-4">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Quick Actions</h3>
              </div>
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => window.location.href = `mailto:${customer.email}`}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <EnvelopeIcon className="h-5 w-5 mr-3" />
                  Send Email
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => window.location.href = `tel:${customer.phone}`}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-white/30 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
                >
                  <PhoneIcon className="h-5 w-5 mr-3" />
                  Call
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openCreateFollowup}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-white/30 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
                >
                  <CalendarIcon className="h-5 w-5 mr-3" />
                  Schedule Meeting
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSendWhatsApp}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-white/30 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
                >
                  <svg className="h-5 w-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  Send WhatsApp
                </motion.button>
              </div>
            </motion.div>
          </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Remark Modal */}
      {remarkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-green-900/80 via-teal-900/80 to-emerald-900/80 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="relative overflow-hidden rounded-t-3xl">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-teal-500 to-emerald-500 opacity-10"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-8">
                  <div className="relative">
                    <div className="p-4 bg-gradient-to-br from-green-500 via-teal-500 to-emerald-500 rounded-3xl shadow-lg">
                      <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-3xl font-bold bg-gradient-to-r from-green-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
                      Add Remark
                    </h3>
                    <p className="text-gray-600 mt-1">Add your notes and observations</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      Remark / Note
                    </label>
                    <textarea
                      value={remarkText}
                      onChange={(e) => setRemarkText(e.target.value)}
                      className="w-full px-5 py-4 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg resize-none"
                      rows={8}
                      placeholder="Enter your remark or note here..."
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-4 pt-8 mt-8 border-t border-gradient-to-r from-green-200 to-teal-200">
                  <button
                    onClick={() => {
                      setRemarkModalOpen(false);
                      setActiveFollowupForRemark(null);
                      setRemarkText('');
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-2xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-1 border border-gray-300/50"
                  >
                    ❌ Cancel
                  </button>
                  <button
                    onClick={handleSaveRemark}
                    className="px-8 py-4 bg-gradient-to-r from-green-500 via-teal-500 to-emerald-500 text-white rounded-2xl hover:from-green-600 hover:via-teal-600 hover:to-emerald-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
                  >
                    ✨ Save Remark
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Followup Modal */}
      {followupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-green-900/80 via-teal-900/80 to-emerald-900/80 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="relative overflow-hidden rounded-t-3xl">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-teal-500 to-emerald-500 opacity-10"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-8">
                  <div className="relative">
                    <div className="p-4 bg-gradient-to-br from-green-500 via-teal-500 to-emerald-500 rounded-3xl shadow-lg">
                      <CalendarIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-3xl font-bold bg-gradient-to-r from-green-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
                      Add Followup
                    </h3>
                    <p className="text-gray-600 mt-1">Schedule your next interaction</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      Title
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={followupData.title}
                        onChange={(e) => setFollowupData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-5 py-4 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg"
                        placeholder="Enter followup title"
                      />
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-green-400">
                        <PencilIcon className="h-5 w-5" />
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                      <div className="w-2 h-2 bg-teal-500 rounded-full mr-2"></div>
                      Note / Description
                    </label>
                    <textarea
                      value={followupData.note}
                      onChange={(e) => setFollowupData(prev => ({ ...prev, note: e.target.value }))}
                      className="w-full px-5 py-4 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg resize-none"
                      rows={4}
                      placeholder="Add any additional notes or description"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        Reminder (optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={followupData.remindAt}
                        onChange={(e) => setFollowupData(prev => ({ ...prev, remindAt: e.target.value }))}
                        className="w-full px-5 py-4 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                        Type
                      </label>
                      <select
                        value={followupData.type}
                        onChange={(e) => setFollowupData(prev => ({ ...prev, type: e.target.value }))}
                        className="w-full px-5 py-4 bg-gradient-to-r from-white to-emerald-50/50 border-2 border-emerald-200 rounded-2xl focus:ring-4 focus:ring-emerald-300/50 focus:border-emerald-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg appearance-none"
                      >
                        <option value="reminder">📝 Reminder</option>
                        <option value="call">📞 Call</option>
                        <option value="email">📧 Email</option>
                        <option value="meeting">👥 Meeting</option>
                        <option value="task">✅ Task</option>
                        <option value="whatsapp">💬 WhatsApp</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="relative">
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                        Priority
                      </label>
                      <select
                        value={followupData.priority}
                        onChange={(e) => setFollowupData(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full px-5 py-4 bg-gradient-to-r from-white to-yellow-50/50 border-2 border-yellow-200 rounded-2xl focus:ring-4 focus:ring-yellow-300/50 focus:border-yellow-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg"
                      >
                        <option value="low">🟢 Low</option>
                        <option value="medium">🟡 Medium</option>
                        <option value="high">🔴 High</option>
                      </select>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                        Assigned To
                      </label>
                      <select
                        value={followupData.assignedTo}
                        onChange={(e) => setFollowupData(prev => ({ ...prev, assignedTo: e.target.value }))}
                        className="w-full px-5 py-4 bg-gradient-to-r from-white to-indigo-50/50 border-2 border-indigo-200 rounded-2xl focus:ring-4 focus:ring-indigo-300/50 focus:border-indigo-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg"
                      >
                        <option value="">👤 Unassigned</option>
                        {users.map(u => (
                          <option key={u._id} value={u._id}>👤 {u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                        Duration (mins)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={followupData.duration}
                        onChange={(e) => setFollowupData(prev => ({ ...prev, duration: Number(e.target.value) }))}
                        className="w-full px-5 py-4 bg-gradient-to-r from-white to-orange-50/50 border-2 border-orange-200 rounded-2xl focus:ring-4 focus:ring-orange-300/50 focus:border-orange-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg"
                        placeholder="30"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-4 pt-8 mt-8 border-t border-gradient-to-r from-green-200 to-teal-200">
                  <button
                    onClick={() => setFollowupModalOpen(false)}
                    className="px-8 py-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-2xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-1 border border-gray-300/50"
                  >
                    ❌ Cancel
                  </button>
                  <button
                    onClick={handleCreateFollowup}
                    className="px-8 py-4 bg-gradient-to-r from-green-500 via-teal-500 to-emerald-500 text-white rounded-2xl hover:from-green-600 hover:via-teal-600 hover:to-emerald-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
                  >
                    ✨ Save Followup
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {modalOpen && (
        <ImageModal
          open={modalOpen}
          src={modalSrc}
          alt={modalFile?.originalName}
          filename={modalFile?.originalName}
          onClose={() => {
            setModalOpen(false);
            if (modalSrc) {
              window.URL.revokeObjectURL(modalSrc);
              setModalSrc(null);
            }
            setModalFile(null);
          }}
          onDownload={() => handleDownloadAttachment(modalFile)}
          onDelete={async () => {
            await handleDeleteAttachment(modalFile);
            setModalOpen(false);
          }}
        />
      )}

      {/* Delete Customer Confirmation Modal */}
      <AnimatePresence>
        {showDeleteCustomerModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowDeleteCustomerModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                  <TrashIcon className="w-8 h-8 text-red-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Delete Customer
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{customer?.name}"</span>?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowDeleteCustomerModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDeleteCustomer}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Attachment Confirmation Modal */}
      <AnimatePresence>
        {showDeleteAttachmentModal && deletingAttachment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowDeleteAttachmentModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                  <PaperClipIcon className="w-8 h-8 text-red-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Delete Attachment
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingAttachment?.originalName}"</span>?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowDeleteAttachmentModal(false);
                      setDeletingAttachment(null);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDeleteAttachment}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomerDetail;
